// components/StatusBar.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const StatusBar: React.FC = () => {
    const { user } = useAuth();
    const [deviceInfo, setDeviceInfo] = useState({ type: '', icon: '', color: '' });
    const [tokenStatus, setTokenStatus] = useState({ isValid: true, expiresIn: '' });
    const [isVisible, setIsVisible] = useState(true);

    // Detectar dispositivo
    useEffect(() => {
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes('zebra') || ua.includes('tc26') || ua.includes('tc21')) {
            setDeviceInfo({ 
                type: 'ZEBRA TC26', 
                icon: 'fa-tablet-alt', 
                color: 'warning'
            });
        } else if (ua.includes('windows')) {
            setDeviceInfo({ 
                type: 'WINDOWS', 
                icon: 'fa-windows', 
                color: 'info'
            });
        } else {
            setDeviceInfo({ 
                type: navigator.platform, 
                icon: 'fa-globe', 
                color: 'secondary'
            });
        }
    }, []);

    // Verificar estado del token
    useEffect(() => {
        const checkToken = () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setTokenStatus({ isValid: false, expiresIn: 'No token' });
                return;
            }

            try {
                // Decodificar token (asumiendo JWT)
                const payload = JSON.parse(atob(token.split('.')[1]));
                const exp = payload.exp * 1000; // convertir a milisegundos
                const now = Date.now();
                const timeLeft = exp - now;

                if (timeLeft <= 0) {
                    setTokenStatus({ isValid: false, expiresIn: 'Expirado' });
                } else {
                    const minutes = Math.floor(timeLeft / 60000);
                    const hours = Math.floor(minutes / 60);
                    
                    if (hours > 0) {
                        setTokenStatus({ 
                            isValid: true, 
                            expiresIn: `${hours}h ${minutes % 60}m` 
                        });
                    } else {
                        setTokenStatus({ 
                            isValid: true, 
                            expiresIn: `${minutes}m` 
                        });
                    }
                }
            } catch (error) {
                setTokenStatus({ isValid: false, expiresIn: 'Inválido' });
            }
        };

        checkToken();
        const interval = setInterval(checkToken, 60000); // Verificar cada minuto
        
        return () => clearInterval(interval);
    }, []);

    if (!user) return null; // No mostrar si no hay usuario logueado

    return (
        <div 
            className="status-bar"
            style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                height: '24px',
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #dee2e6',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 12px',
                zIndex: 1000,
                boxShadow: '0 -2px 5px rgba(0,0,0,0.05)'
            }}
        >
            {/* Botón para ocultar/mostrar (opcional) */}
            <button
                onClick={() => setIsVisible(!isVisible)}
                style={{
                    border: 'none',
                    background: 'none',
                    padding: '0 4px',
                    cursor: 'pointer',
                    fontSize: '10px',
                    marginRight: '8px'
                }}
            >
                <i className={`fas fa-chevron-${isVisible ? 'down' : 'up'}`}></i>
            </button>

            {isVisible && (
                <>
                    {/* Dispositivo */}
                    <span className="me-3" title="Dispositivo">
                        <i className={`fas ${deviceInfo.icon} text-${deviceInfo.color} me-1`}></i>
                        <span className="d-none d-sm-inline">{deviceInfo.type}</span>
                    </span>

                    {/* Token */}
                    <span className="me-3" title="Estado del token">
                        <i className={`fas fa-key me-1 ${tokenStatus.isValid ? 'text-success' : 'text-danger'}`}></i>
                        <span className="d-none d-sm-inline">
                            Token: {tokenStatus.expiresIn}
                        </span>
                    </span>

                    {/* Usuario */}
                    <span className="me-3 d-none d-md-inline" title="Usuario">
                        <i className="fas fa-user me-1"></i>
                        {user.User.email}
                    </span>

                    {/* Versión de la app */}
                    <span className="me-3 d-none d-lg-inline" title="Versión">
                        <i className="fas fa-code-branch me-1"></i>
                        v{process.env.REACT_APP_VERSION || '1.0.0'}
                    </span>

                    {/* Entorno */}
                    <span className="ms-auto" title="Entorno">
                        <span className={`badge bg-${process.env.NODE_ENV === 'production' ? 'success' : 'warning'} text-white`}>
                            {process.env.NODE_ENV}
                        </span>
                    </span>
                </>
            )}
        </div>
    );
};

export default StatusBar;